/* eslint-disable no-console */
import React, { useEffect, isValidElement } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';
import {
    Header,
    Footer,
} from '@usb-onboarding/product-recommender-components';
import USBTabs from '@usb-shield/react-tabs';

import {
    getBankerRecommendedResults,
    getAllBankerProducts,
    setProductFilterList,
    updateFilteredResults,
    resetNavigationToFirstQuestion,
} from '../../redux/actions';
import Loader from '../../atoms/Loader';
import RelationshipDetail from '../../molecules/RelationshipDetail';
import SendEmail from '../../molecules/SendEmail';
import DpsProductGroup from '../../molecules/DpsProductGroup';
import { productConfiguration } from '../../config';
import defaultLang from '../../language';
import { productBPSID, timeoutRedirectTo } from '../../constants';
import {
    getFilterOptionList,
    getFilteredListOfProducts,
    sortByProductOrder,
    resetCorrelationId,
    filterInvalidQuestions,
    getDPSResultsReportingData,
    getDPSResultsWithAnswersReportingData,
    dispatchReportingEvent,
    tealiumUpdatedPageView,
    mapProductsSelected,
    getDPSResultsOnClickEventReportingData,
    constructOnClickEvent,
    constructAnswerResponse,
    tealiumUpdatedClick,
} from '../../utils';
import DpsCategoryFilter from '../../atoms/DpsCategoryFilter/DpsCategoryFilter';
import '@usb-shield/react-tabs/dist/library/styles/index.css';
import './bankerRecommendations.scss';

function BankerRecommendations() {
    const dispatch = useDispatch();
    const history = useHistory();
    const [activeTab, setActiveTab] = React.useState(
        'recommendedProducts',
    );
    const error = useSelector((state) => state.errorReducer.error);
    const relationshipName = useSelector(
        (state) => state.dynamicLandingReducer.relationshipName,
    );
    const { navigateToFirstQuestion } = useSelector(
        (state) => state.appReducer,
    );
    const answerContainer = useSelector(
        (state) => state.appReducer.answerContainer,
    );
    const filterList = useSelector(
        (state) => state.recommendationReducer.filterList,
    );

    const recommendedProduct = useSelector(
        (state) => state.recommendationReducer.recommendedResults,
    );
    const allProducts = useSelector(
        (state) => state.recommendationReducer.allProducts,
    );
    const filteredResults = useSelector(
        (state) => state.recommendationReducer.filteredResults,
    );

    const AEMContent = useSelector(
        (state) => state.AEMContentReducer.AEMContent,
    );
    const sessionID = useSelector(
        (state) => state.dynamicLandingReducer.sessionID,
    );
    const isLoading = useSelector(
        (state) => state.loaderReducer.isLoading,
    );
    const activeQuestionID = useSelector(
        (state) => state.dynamicLandingReducer.activeQuestionID,
    );
    const isAllProductsActive = activeTab === 'allProducts';
    const reportingDataResultsHandler = (errorMessage) => {
        const currentPage = defaultLang.siteCat.suggestedProductsPage;
        let reportingData;
        const answerResponse =
            constructAnswerResponse(answerContainer);
        if (errorMessage) {
            reportingData = getDPSResultsReportingData(
                currentPage,
                mapProductsSelected(filteredResults),
                errorMessage,
                answerResponse,
            );
            dispatchReportingEvent(reportingData);
            tealiumUpdatedClick();
        } else {
            reportingData = getDPSResultsWithAnswersReportingData(
                currentPage,
                mapProductsSelected(filteredResults),
                answerResponse,
            );
            dispatchReportingEvent(reportingData);
            tealiumUpdatedPageView();
        }

        window.scrollTo(0, 0);
    };

    useEffect(() => {
        if (filteredResults.length) {
            reportingDataResultsHandler();
        }
    }, [filteredResults]);

    const reportingDataOnClickEventHandler = (event, productId) => {
        const currentPage = defaultLang.siteCat.suggestedProductsPage;
        const tab =
            activeTab === 'allProducts'
                ? defaultLang.siteCat.otherTabOption
                : defaultLang.siteCat.defaultTabOption;
        const onClickEvent = constructOnClickEvent(
            currentPage,
            tab,
            event,
            productId,
        );
        const reportingData = getDPSResultsOnClickEventReportingData(
            currentPage,
            mapProductsSelected(filteredResults),
            onClickEvent,
        );
        dispatchReportingEvent(reportingData);
        tealiumUpdatedClick();
        window.scrollTo(0, 0);
    };
    function handleStatusUpdateFilter(result) {
        dispatch(setProductFilterList(result.options));
    }
    useEffect(() => {
        const selctedAnswers = Object.values(answerContainer).map(
            (values) =>
                Object.keys(values).map((val) => {
                    const ans = values[val]?.map((filterval) => ({
                        id: filterval?.value || filterval,
                    }));
                    return { questionId: val, answers: ans };
                }),
        );
        const payload = {
            productID: productConfiguration[productBPSID],
            sessionID,
            customerID: sessionID,
            selectedQuestionnaire: filterInvalidQuestions(
                selctedAnswers.flat(1),
            ),
        };
        dispatch(getBankerRecommendedResults(payload));
        window.scrollTo(0, 0);
    }, []);
    useEffect(() => {
        if (activeTab === 'allProducts' && !allProducts.length) {
            const payload = {
                productID: productConfiguration[productBPSID],
                sessionID,
            };
            dispatch(getAllBankerProducts(payload));
            window.scrollTo(0, 0);
        }
    }, [activeTab, allProducts]);
    const getProductFilterList = (activeFilters = []) => {
        const setActiveFilters = (productList) => {
            if (activeFilters.length === 0) {
                return productList;
            }
            productList.forEach((filter) => {
                if (activeFilters.includes(filter.name)) {
                    // eslint-disable-next-line no-param-reassign
                    filter.active = true;
                }
            });
            return productList;
        };
        if (
            activeTab === 'recommendedProducts' &&
            recommendedProduct.length > 0
        ) {
            const productListItems = {
                productList: recommendedProduct,
            };
            let productList = getFilterOptionList(productListItems);
            productList = setActiveFilters(productList);
            return productList;
        }
        if (activeTab === 'allProducts' && allProducts.length > 0) {
            const productListItems = {
                productList: allProducts,
            };
            let productList = getFilterOptionList(productListItems);
            productList = setActiveFilters(productList);
            return productList;
        }

        return [];
    };
    useEffect(() => {
        const productList = getProductFilterList();
        if (productList) {
            dispatch(setProductFilterList(productList));
        }
    }, [recommendedProduct, allProducts, activeTab]);

    useEffect(() => {
        if (
            activeTab === 'recommendedProducts' &&
            filterList.length
        ) {
            const filteredProductsList = getFilteredListOfProducts(
                recommendedProduct,
                filterList,
            );
            dispatch(updateFilteredResults(filteredProductsList));
        }
        if (activeTab === 'allProducts' && filterList.length) {
            const filteredProductsList = getFilteredListOfProducts(
                allProducts,
                filterList,
            );
            dispatch(updateFilteredResults(filteredProductsList));
        }
    }, [filterList, activeTab]);

    useEffect(() => {
        // adding a setTimeout around this navigation because, when clicking on startover link,
        // we are clearing out redux state, which will clear out sessionId,
        // and this useEffect will execute before we dispatch another action (initiateQuestionnaireDynamicLanding (in initiateStartOver method)) which will
        // fetch the first question of the questionnaire from API
        let redirectTo = '';
        let navigationTimeoutId;
        if (!sessionID) {
            redirectTo = timeoutRedirectTo;
        } else if (error) {
            redirectTo = '/banker/error';
            resetCorrelationId();
        }
        if (redirectTo) {
            navigationTimeoutId = setTimeout(
                () => history.push(redirectTo),
                1000,
            );
        }

        return () => {
            if (navigationTimeoutId) {
                clearTimeout(navigationTimeoutId);
            }
        };
    }, [error, sessionID]);
    const handleClickOnTab = (e) => {
        const { id } = e.target;
        const currentIndex = id.split('result-id_')[1];
        switch (currentIndex) {
            case '0':
                setActiveTab('recommendedProducts');
                break;
            case '1':
                setActiveTab('allProducts');
                break;
            default:
        }
    };

    useEffect(() => {
        if (activeQuestionID && navigateToFirstQuestion) {
            dispatch(resetNavigationToFirstQuestion());
            history.push(`/banker/questionnaire/${activeQuestionID}`);
        }
    }, [activeQuestionID]);

    const getParsedFilterList = () => {
        if (
            filterList &&
            filterList.length > 0 &&
            !isValidElement(filterList[0].label)
        ) {
            const activeFilters = [];
            filterList.forEach((filter) => {
                if (filter.active) {
                    activeFilters.push(filter.name);
                }
            });
            return getProductFilterList(activeFilters);
        }
        return filterList;
    };
    return (
        <>
            {isLoading && <Loader />}
            <div className="banker-container-header">
                <Header title={defaultLang.header.fullName} />
                <Helmet>
                    <title>
                        We have great options | Business Product |
                        U.S. Bank
                    </title>
                </Helmet>
            </div>
            <main id="app__content" className="main-content-wrapper">
                <div className="main-left-content">
                    <h1>
                        <legend>
                            {defaultLang.recommendation.title}
                        </legend>
                    </h1>
                    <USBTabs
                        id="result-tab"
                        className="product-tabs-wrapper"
                        handleClick={handleClickOnTab}
                        tabId="result-id"
                        tabs={defaultLang.tabsHeader}
                    >
                        <div>
                            {activeTab !== 'allProducts' && (
                                <>
                                    <section>
                                        {filterList.length > 0 && (
                                            <DpsCategoryFilter
                                                componentRef={`${activeTab}${Math.random()}`}
                                                productList={sortByProductOrder(
                                                    getParsedFilterList(),
                                                )}
                                                handleStatusUpdate={
                                                    handleStatusUpdateFilter
                                                }
                                            />
                                        )}
                                    </section>
                                    <section>
                                        {filteredResults.length >
                                            0 && (
                                            <DpsProductGroup
                                                reportingDataOnClickEvent={
                                                    reportingDataOnClickEventHandler
                                                }
                                                keyName="_recommendedlists"
                                                filteredResults={
                                                    filteredResults
                                                }
                                                isAllProductsActive={
                                                    isAllProductsActive
                                                }
                                            />
                                        )}
                                    </section>
                                </>
                            )}
                        </div>
                        <div>
                            {activeTab === 'allProducts' && (
                                <>
                                    <section>
                                        {filterList.length > 0 && (
                                            <DpsCategoryFilter
                                                componentRef={`${activeTab}${Math.random()}`}
                                                productList={sortByProductOrder(
                                                    filterList,
                                                    isAllProductsActive,
                                                )}
                                                handleStatusUpdate={
                                                    handleStatusUpdateFilter
                                                }
                                            />
                                        )}
                                    </section>
                                    <section>
                                        {filteredResults.length >
                                            0 && (
                                            <DpsProductGroup
                                                reportingDataOnClickEvent={
                                                    reportingDataOnClickEventHandler
                                                }
                                                filteredResults={
                                                    filteredResults
                                                }
                                                keyName="_alllist"
                                                isAllProductsActive={
                                                    isAllProductsActive
                                                }
                                            />
                                        )}
                                    </section>
                                </>
                            )}
                        </div>
                    </USBTabs>
                </div>
                <div className="main-right-content">
                    <div>
                        <RelationshipDetail name={relationshipName} />
                    </div>
                    <div>
                        <SendEmail
                            reportingDataOnClickEvent={
                                reportingDataOnClickEventHandler
                            }
                            onReportingDataResultsHandler={
                                reportingDataResultsHandler
                            }
                        />
                    </div>
                </div>
            </main>
            <div className="banker-container-footer">
                <Footer
                    header={defaultLang.footer.connectionTitle}
                    content={defaultLang.footer.footerDisclosure}
                    privacyLabel={AEMContent.footer.privacyLabel}
                    privacyUrl={AEMContent.footer.privacyUrl}
                    fdic={defaultLang.footer.connectionContent}
                    cobrowseLabel={AEMContent.footer.cobrowseLabel}
                    cobrowseUrl={AEMContent.footer.cobrowseUrl}
                />
            </div>
        </>
    );
}

export default BankerRecommendations;
